/**
 * Автосохранение профиля (ТЗ FR-03).
 *
 * Сохранение выполняется при уходе фокуса с поля и не реже одного раза
 * в 60 секунд. Пользователь не должен помнить про кнопку «Сохранить»:
 * дело заполняется в течение получаса, и потерять его недопустимо.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { CaseProfile } from '@bd/domain';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface AutosaveOptions {
  dirty: boolean;
  profile: CaseProfile | null;
  save: () => Promise<CaseProfile | null>;
  readOnly: boolean;
  /** Период принудительного сохранения, мс */
  intervalMs?: number;
}

export function useAutosave({ dirty, profile, save, readOnly, intervalMs = 60_000 }: AutosaveOptions) {
  const [status, setStatus] = useState<SaveStatus>('idle');
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const saving = useRef(false);

  const flush = useCallback(async () => {
    if (readOnly || saving.current || !dirty || !profile) return;
    saving.current = true;
    setStatus('saving');
    try {
      // Предыдущая редакция уходит в соседнее поле внутри самого сохранения:
      // отдельный вызов резервного копирования больше не нужен
      await save();
      setStatus('saved');
      setSavedAt(new Date());
      setError(null);
    } catch (e) {
      setStatus('error');
      setError((e as Error).message);
    } finally {
      saving.current = false;
    }
  }, [readOnly, dirty, profile, save]);

  // Периодическое сохранение
  useEffect(() => {
    const timer = setInterval(() => {
      void flush();
    }, intervalMs);
    return () => clearInterval(timer);
  }, [flush, intervalMs]);

  // Сохранение при уходе фокуса с любого поля карточки
  useEffect(() => {
    const handler = (event: FocusEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && ['INPUT', 'SELECT', 'TEXTAREA'].includes(target.tagName)) void flush();
    };
    document.addEventListener('focusout', handler);
    return () => document.removeEventListener('focusout', handler);
  }, [flush]);

  // Предупреждение при закрытии вкладки с несохранёнными данными
  useEffect(() => {
    const handler = (event: BeforeUnloadEvent) => {
      if (dirty) event.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  return { status, savedAt, error, flush };
}
